import {
  buildOrderDeskPackingSlipHTML,
  PACKING_SLIP_DEFAULT_LOGO_URL,
  withNormalizedPackingSlipMargins,
} from '../utils/packingSlipHtml';
import { buildCheetahLz17PackingSlipHTML } from '../utils/cheetahLz17PackingSlipHtml';
import { buildGo4OutsideLabelHTML } from '../utils/go4OutsideLabelHtml';
import { prepareHtmlForNativeBatchPrint } from '../utils/nativeBatchPrint';
import { buildGiftCardPrintHtml } from '../utils/giftCardPrintHtml';
import { buildEmbeddedGiftCardHtml } from '../utils/printAssetInlining';
import { buildPrintSeparatorHtml } from '../utils/printJobSeparators';
import { countPhysicalPagesByOrderFromHtml } from '../utils/printHtmlToPdf';
import { getPrintQueueBlob } from './printQueueStore';

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Unable to read queued PDF.'));
    reader.readAsDataURL(blob);
  });

export async function materializePrintQueueItem(item) {
  const recipe = item.recipe || {};
  const order = item.orderSnapshot || recipe.order;
  if (
    item.kind === 'packing-slip' ||
    item.kind === 'cheetah-label' ||
    item.kind === 'go4-outside-label'
  ) {
    if (!order) throw new Error(`${item.label} no longer has an order snapshot.`);
    const copies = Math.max(1, Math.min(99, Number(recipe.copies) || 1));
    const html =
      item.kind === 'cheetah-label'
        ? await buildCheetahLz17PackingSlipHTML([order], {
            logoUrl: recipe.logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL,
            showPrices: recipe.settings?.showPrices !== false,
            showSignatureLines: recipe.settings?.showSignatureLines !== false,
            enrichAccounts: true,
          })
        : item.kind === 'go4-outside-label'
          ? await buildGo4OutsideLabelHTML([order], {
              logoUrl: recipe.logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL,
              showSignatureLines: recipe.settings?.showSignatureLines !== false,
              enrichAccounts: true,
            })
          : await buildOrderDeskPackingSlipHTML([order], {
              logoUrl: recipe.logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL,
              settings: withNormalizedPackingSlipMargins(recipe.settings),
              weightsData: recipe.weightsData || {},
            });
    const orderKey = item.orderKey || recipe.orderKey;
    const printableHtml = prepareHtmlForNativeBatchPrint(html, {
      copiesPerOrder: copies,
      copiesByOrder: orderKey ? { [orderKey]: copies } : {},
    });
    const physicalPagesByOrder = countPhysicalPagesByOrderFromHtml(html);
    return {
      payloadType: 'html',
      html: printableHtml,
      byteLength: new Blob([printableHtml]).size,
      physicalPages:
        Number(physicalPagesByOrder[orderKey]) ||
        Number(Object.values(physicalPagesByOrder)[0]) ||
        1,
      release: () => {},
    };
  }
  if (item.kind === 'gift-card') {
    const html = await buildEmbeddedGiftCardHtml(buildGiftCardPrintHtml, [
      {
        order,
        orderKey: item.orderKey || recipe.orderKey,
        message: recipe.message,
        sourceLabel: recipe.sourceLabel,
      },
    ]);
    return {
      payloadType: 'html',
      html,
      byteLength: new Blob([html]).size,
      release: () => {},
    };
  }
  if (item.kind === 'separator') {
    const html = buildPrintSeparatorHtml(recipe.group || {}, recipe.options || {});
    return {
      payloadType: 'html',
      html,
      byteLength: new Blob([html]).size,
      release: () => {},
    };
  }
  if (item.kind === 'pick-list') {
    const html = String(recipe.html || '');
    if (!html.trim()) throw new Error(`${item.label} has no pick-list HTML.`);
    return {
      payloadType: 'html',
      html,
      byteLength: new Blob([html]).size,
      release: () => {},
    };
  }
  if (item.kind === 'carrier-pdf' || item.kind === 'general-pdf') {
    const blob = await getPrintQueueBlob(recipe.blobRef);
    if (!blob) throw new Error(`Queued PDF data for ${item.label} is unavailable.`);
    const dataUrl = await blobToDataUrl(blob);
    return {
      payloadType: 'pdf',
      pdfBase64: dataUrl,
      byteLength: blob.size,
      pageCount: Number(recipe.pageCount) || 1,
      pageRanges: recipe.pageRanges || '',
      release: () => {},
    };
  }
  if (item.kind === 'general-html') {
    const html = String(recipe.html || '');
    if (!html.trim()) throw new Error(`${item.label} has no HTML content.`);
    return {
      payloadType: 'html',
      html,
      byteLength: new Blob([html]).size,
      release: () => {},
    };
  }
  if (item.kind === 'general-docx') {
    return {
      payloadType: 'skip',
      skipReason:
        'Word .docx is held in the mixed queue for continuity. Export or save as PDF on this paper size to print the slot through QZ.',
      byteLength: 0,
      release: () => {},
    };
  }
  throw new Error(`Unsupported queued print type: ${item.kind}`);
}

export default materializePrintQueueItem;
